import Data, { IUploadSession, IUploadSessionFile, IUploadedPart } from '../../api/Data'

const SIGNING_BATCH_SIZE = 10
const UPLOAD_CONCURRENCY = 3

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = ''
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)))
  return window.btoa(binary)
}

export const normaliseVideoContentType = (file: File) => {
  const declared = file.type.split(';')[0].trim().toLowerCase()
  if (declared === 'video/avi') return 'video/x-msvideo'
  if (declared) return declared
  const extension = file.name.split('.').pop()!.toLowerCase()
  return ({ avi: 'video/x-msvideo', mov: 'video/quicktime', mp4: 'video/mp4', webm: 'video/webm' } as any)[extension]
}

const checksumBlob = async (blob: Blob) => {
  if (!window.crypto?.subtle) throw new Error('Secure upload requires HTTPS or localhost')
  const digest = await window.crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return bytesToBase64(new Uint8Array(digest))
}

const uploadPart = (url: string, blob: Blob, checksumSha256: string, onProgress: (loaded: number) => void) =>
  new Promise<string>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', url)
    request.setRequestHeader('x-amz-checksum-sha256', checksumSha256)
    request.upload.onprogress = (event) => event.lengthComputable && onProgress(event.loaded)
    request.onerror = () => reject(new Error('S3 upload failed'))
    request.onabort = () => reject(new Error('S3 upload cancelled'))
    request.onload = () => {
      if (request.status < 200 || request.status >= 300)
        return reject(new Error(`S3 upload failed (${request.status})`))
      const eTag = request.getResponseHeader('ETag')
      if (!eTag) return reject(new Error('S3 upload did not return an ETag'))
      onProgress(blob.size)
      resolve(eTag)
    }
    request.send(blob.type ? blob.slice(0, blob.size, '') : blob)
  })

const runWithConcurrency = async <T>(items: T[], worker: (item: T) => Promise<void>) => {
  let nextIndex = 0
  const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, items.length) }, async () => {
    while (nextIndex < items.length) await worker(items[nextIndex++])
  })
  await Promise.all(workers)
}

const requireData = <T>(result: { data: T | null; error: any }): T => {
  if (result.error || !result.data) throw result.error || new Error('Upload API returned no data')
  return result.data
}

export const uploadFileToSession = async ({
  session,
  sourceAsset,
  file,
  onProgress,
}: {
  session: IUploadSession
  sourceAsset: IUploadSessionFile
  file: File
  onProgress: (loaded: number, total: number) => void
}) => {
  const totalParts = Math.ceil(file.size / sourceAsset.partSizeBytes)
  const completedParts: IUploadedPart[] = []
  const loadedByPart = new Map<number, number>()
  const report = () =>
    onProgress(
      Array.from(loadedByPart.values()).reduce((sum, value) => sum + value, 0),
      file.size
    )

  for (let offset = 0; offset < totalParts; offset += SIGNING_BATCH_SIZE) {
    const prepared = []
    for (let partNumber = offset + 1; partNumber <= Math.min(offset + SIGNING_BATCH_SIZE, totalParts); partNumber++) {
      const start = (partNumber - 1) * sourceAsset.partSizeBytes
      const blob = file.slice(start, Math.min(start + sourceAsset.partSizeBytes, file.size))
      prepared.push({ partNumber, blob, checksumSha256: await checksumBlob(blob) })
    }
    const signed = requireData(
      await Data.signUploadSessionParts(
        session.id,
        sourceAsset.id,
        session.token,
        prepared.map(({ partNumber, checksumSha256 }) => ({ partNumber, checksumSha256 }))
      )
    )
    const signedByPart = new Map(signed.map((part) => [part.partNumber, part]))
    await runWithConcurrency(prepared, async (part) => {
      const signedPart = signedByPart.get(part.partNumber)
      if (!signedPart) throw new Error(`Missing signed URL for part ${part.partNumber}`)
      const eTag = await uploadPart(signedPart.url, part.blob, part.checksumSha256, (loaded) => {
        loadedByPart.set(part.partNumber, loaded)
        report()
      })
      completedParts.push({ partNumber: part.partNumber, eTag, checksumSha256: part.checksumSha256 })
    })
  }

  requireData(await Data.completeUploadSessionFile(session.id, sourceAsset.id, session.token, completedParts))
  onProgress(file.size, file.size)
}

export const createDeviceMetadata = () => ({
  language: window.navigator.language || null,
  platform: window.navigator.platform || null,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
  userAgent: window.navigator.userAgent || null,
})

export const captureVideoPoster = (file: File) =>
  new Promise<string | null>((resolve) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const objectUrl = URL.createObjectURL(file)
    let hasFinished = false
    let timeout: number

    const finish = (poster: string | null) => {
      if (hasFinished) return
      hasFinished = true
      window.clearTimeout(timeout)
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(objectUrl)
      resolve(poster)
    }

    timeout = window.setTimeout(() => finish(null), 8000)

    video.muted = true
    video.playsInline = true
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      video.currentTime =
        Number.isFinite(video.duration) && video.duration > 0 ? Math.min(0.1, video.duration / 2) : 0.1
    }
    video.onseeked = () => {
      if (!video.videoWidth || !video.videoHeight) return finish(null)
      const scale = Math.min(1, 640 / video.videoWidth)
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
      const context = canvas.getContext('2d')
      if (!context) return finish(null)
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      try {
        finish(canvas.toDataURL('image/jpeg', 0.75))
      } catch (_) {
        finish(null)
      }
    }
    video.onerror = () => finish(null)
    video.src = objectUrl
    video.load()
  })
